import { Container } from 'pixi.js';
import { Signal } from 'typed-signals';
import type { TileStackModel } from '../../models/board/TileStackModel';
import { TileStackShadow } from '../board/TileStackShadow';
import { TILE_Z_OFFSET } from '../board/TileView';
import type { TileView } from '../board/TileView';
import { Easing } from '../../../../libs/TweenManager/easing';
import type { Tween } from '../../../../libs/TweenManager/Tween';
import type { BoardViewFactory } from '../board/BoardViewFactory';

export class TrayStackView extends Container {
  public readonly shadow: TileStackShadow = new TileStackShadow().props({
    parent: this,
  });

  public readonly tiles = new Container().props({
    parent: this,
  });

  public locked = false;

  private scaleTween: Tween | null = null;

  private shadowTween: Tween | null = null;

  public readonly onPointerDown = new Signal<(stack: TrayStackView) => void>();

  constructor(
    public readonly model: TileStackModel,
    private readonly viewFactory: BoardViewFactory,
  ) {
    super();
    this.updateTiles();
    this.addPointerEvents();
    this.shadow.update(model.getTileCount());
  }

  private addPointerEvents(): void {
    this.tiles.interactive = true;
    this.tiles.interactiveChildren = true;
    this.interactive = true;
    this.on('pointerdown', () => {
      if (this.locked) return;
      this.onPointerDown.emit(this);
    });
  }

  public updateTiles(): void {
    const tiles = this.model.getTiles();

    // add pooling
    this.tiles.children.slice().forEach((tileView) => {
      tileView.destroy({ children: true });
    });

    tiles.forEach((tile) => {
      this.viewFactory.createTileView(tile).props({
        parent: this.tiles,
        x: 0,
        y: -this.tiles.children.length * TILE_Z_OFFSET,
      });
    });

    this.shadow.update(tiles.length);
  }

  public animateToScale(
    pickedUp: boolean,
    toScale: number,
    delay = 0,
    duration = 333,
    ease: (amount: number) => number = Easing.Quadratic.Out,
  ): void {
    this.killScaleTween();

    this.scaleTween = this.tweenTo({ scaleX: toScale, scaleY: toScale }, duration).delay(delay).easing(ease).start();

    if (!this.shadow) return;

    const shadowScale = pickedUp ? 0 : 1;
    this.shadowTween = this.shadow.tweenTo(
      { scaleX: shadowScale, scaleY: shadowScale },
      duration,
      'quadOut',
      true,
      delay,
    );
  }

  public getTileViews(): TileView[] {
    return this.tiles.children.slice() as TileView[];
  }

  public killScaleTween(): void {
    this.scaleTween?.stop();
    this.shadowTween?.stop();
  }
}
